import { useMemo, useState } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import { formatPrice } from "../../../../utilities/formatPrice";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import { buildSearchText, formatDay, getToken } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import CatalogTable from "../../components/list/CatalogTable";
import StatusBadge from "../../components/list/StatusBadge";

// Mesmos ids de meio de pagamento consultados pelo admin clássico
const METHODS = [
  { key: "paypal", id: 1, label: () => "PayPal" },
  { key: "voucher", id: 2, label: () => "Voucher" },
  { key: "wallet", id: 4, label: () => localeText("wallet") },
];

const PLAN_TYPES = {
  FIRST: { labelKey: "initialSub", tone: "good" },
  RENEW: { labelKey: "annualRenewal", tone: "neutral" },
  TRIAL: { labelKey: "test", tone: "warning" },
};

// Carrega os três meios em paralelo; um meio com erro não impede os outros de aparecer
const loadAllMethods = async () => {
  const service = new AdminService();
  const results = await Promise.allSettled(
    METHODS.map(async (method) =>
      expectCode(await service.getPaidSubscriptions(getToken(), method.id), 200001).result || [],
    ),
  );
  const failed = results.find((result) => result.status === "rejected");
  if (results.every((result) => result.status === "rejected")) throw failed.reason;

  return [
    Object.fromEntries(
      METHODS.map((method, index) => [
        method.key,
        results[index].status === "fulfilled" ? results[index].value : null,
      ]),
    ),
  ];
};

const toRow = (subscription, index) => ({
  ...subscription,
  id: subscription.id ?? `row-${index}`,
  fullName: `${subscription.user?.firstname || ""} ${subscription.user?.surname || ""}`.trim(),
  username: subscription.user?.username,
  planDescription: subscription.plan?.description,
  dateValue: subscription.datePayment ? new Date(subscription.datePayment).getTime() || 0 : 0,
  amountValue: Number(subscription.amountPaid) || 0,
  searchText: buildSearchText([
    subscription.user?.username,
    subscription.user?.firstname,
    subscription.user?.surname,
    subscription.plan?.description,
  ]),
});

const AdminPaidSubscriptions = () => {
  const { items, loading, loadError, reload } = useAdminList(loadAllMethods);
  const [tab, setTab] = useState(METHODS[0].key);
  const data = useMemo(() => items[0] || {}, [items]);

  const rowsByMethod = useMemo(
    () =>
      Object.fromEntries(
        METHODS.map((method) => [method.key, (data[method.key] || []).map(toRow)]),
      ),
    [data],
  );

  const counts = Object.fromEntries(
    METHODS.map((method) => [method.key, rowsByMethod[method.key].length]),
  );

  const rows = rowsByMethod[tab];
  const methodFailed = !loading && data[tab] === null;
  const total = rows.reduce((sum, row) => sum + row.amountValue, 0);

  return (
    <div className="admin-list">
      <StatusTabs
        tabs={METHODS.map((method) => ({ value: method.key, label: method.label() }))}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      {!loading && rows.length > 0 && (
        <p className="admin-muted admin-subscriptions__total">
          {localeText("adminReportsTotalPaid")}: <strong>{formatPrice(total)}</strong>
        </p>
      )}

      <CatalogTable
        key={tab}
        rows={rows}
        loading={loading}
        loadError={loadError || methodFailed}
        onReload={reload}
        searchPlaceholder={localeText("adminSubscriptionsSearch")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="56rem"
      >
        <Column
          header={localeText("adminUsersUser")}
          field="fullName"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">{row.fullName || row.username || "—"}</span>
              {row.username && <span className="admin-muted">@{row.username}</span>}
            </div>
          )}
          sortable
        />
        <Column header={localeText("plan")} field="planDescription" sortable />
        <Column
          header={localeText("type")}
          field="typePaymentPlan"
          body={(row) => {
            const type = PLAN_TYPES[row.typePaymentPlan];
            return type ? (
              <StatusBadge tone={type.tone}>{localeText(type.labelKey)}</StatusBadge>
            ) : (
              <span className="admin-muted">{row.typePaymentPlan || "—"}</span>
            );
          }}
          sortable
        />
        <Column
          header={localeText("date")}
          field="dateValue"
          body={(row) => formatDay(row.datePayment)}
          sortable
        />
        <Column
          header={localeText("amount")}
          field="amountValue"
          body={(row) => <span className="admin-num admin-strong">{formatPrice(row.amountValue)}</span>}
          sortable
        />
        <Column
          header={localeText("paymentMethod")}
          field="paymentMethod.description"
          body={(row) => row.paymentMethod?.description || "—"}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminPaidSubscriptions;
